import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Shield, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export default function AdminForgotPasswordPage() {
  const [email, setEmail] = useState("admin@xpert.company");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [tokenCopied, setTokenCopied] = useState(false);
  const [step, setStep] = useState(1);

  // Password strength matching backend requirements
  const passwordStrength = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
  };
  const passwordValid = Object.values(passwordStrength).every(Boolean);

  const handleCopyToken = () => {
    navigator.clipboard.writeText(resetToken);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter admin email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/forgot-password`, { email });
      const token = response.data.reset_token;
      if (token) {
        setResetToken(token);
        setStep(2);
        toast.success("Reset token generated — copy it below!");
      } else {
        // Email not found — don't reveal but show safe message
        toast.info("If that admin email exists, a token would be shown here.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      toast.error("Password must be 8+ chars with uppercase, lowercase, and a number");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { 
        email,
        reset_token: resetToken,
        new_password: newPassword
      });
      setSubmitted(true);
      toast.success("Admin password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      {/* Header */}
      <header className="bg-canvas-card/50 border-b border-slateline-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <span className="font-display text-xl text-ink">Xpert</span>
          </Link>
          <Link 
            to="/admin/login" 
            className="flex items-center gap-2 text-slateline-500 hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Login
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-brand-green" />
            </div>
            <h1 className="font-display text-3xl mb-2">Admin Password Reset</h1>
            <p className="text-slateline-500">
              {submitted 
                ? "Your admin password has been reset" 
                : step === 1 
                  ? "Verify your admin email to get a reset token"
                  : "Copy your token and set a new password"
              }
            </p>
          </div>

          <div className="bg-canvas-card border border-slateline-200 p-8 rounded-sm">
            {submitted ? (
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-brand-green mx-auto mb-4" />
                <p className="text-slateline-600 mb-6">Your admin password has been successfully reset.</p>
                <Link to="/admin/login">
                  <Button className="w-full bg-brand-green hover:bg-brand-greenHover text-white font-semibold py-6 rounded-sm">
                    Go to Admin Login
                  </Button>
                </Link>
              </div>
            ) : step === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Admin Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slateline-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@xpert.company"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 bg-canvas border-slateline-200 focus:border-brand-green h-12"
                      data-testid="admin-forgot-email"
                    />
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-green hover:bg-brand-greenHover text-white font-semibold py-6 rounded-sm"
                  data-testid="admin-forgot-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Generate Reset Token"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                {/* Token display — prominent and copyable */}
                <div className="p-4 bg-brand-green/10 border border-brand-green/40 rounded-sm">
                  <p className="text-xs text-slateline-500 mb-2">Your one-time reset token (valid for 1 hour):</p>
                  <div className="flex items-center gap-3">
                    <code className="flex-1 text-brand-green text-2xl font-mono font-bold tracking-[0.3em] text-center bg-canvas px-4 py-3 rounded-sm">
                      {resetToken}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopyToken}
                      className="text-xs px-3 py-2 bg-slateline-200 hover:bg-slateline-300 text-white rounded-sm transition-colors shrink-0"
                    >
                      {tokenCopied ? "✓ Copied" : "Copy"}
                    </button>
                  </div>
                  <p className="text-xs text-amber-400/70 mt-2">⚠ Save this token — it won't be shown again</p>
                </div>

                {/* New password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Admin Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Create a strong password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-canvas border-slateline-200 focus:border-brand-green h-12"
                    data-testid="admin-new-password"
                  />
                  {/* Password strength checklist */}
                  {newPassword && (
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {[
                        { ok: passwordStrength.minLength, label: "8+ characters" },
                        { ok: passwordStrength.hasUppercase, label: "Uppercase letter" },
                        { ok: passwordStrength.hasLowercase, label: "Lowercase letter" },
                        { ok: passwordStrength.hasNumber, label: "Number" },
                      ].map(({ ok, label }) => (
                        <div key={label} className={`flex items-center gap-1 text-xs ${ok ? "text-brand-green" : "text-slateline-400"}`}>
                          <span>{ok ? "✓" : "○"}</span>
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button 
                  type="submit"
                  disabled={loading || !passwordValid}
                  className="w-full bg-brand-green hover:bg-brand-greenHover text-white font-semibold py-6 rounded-sm disabled:opacity-50"
                  data-testid="admin-reset-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Reset Admin Password"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep(1); setResetToken(""); }}
                  className="w-full text-center text-sm text-slateline-500 hover:text-ink transition-colors"
                >
                  ← Try a different email
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-slateline-400 text-sm mt-6">
            Contact support if you need additional help.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
